export const FORMULAS = [
    {name: 'TRIM', sig: 'TRIM(field)', desc: 'Remove leading & trailing spaces'},
    {name: 'UPPER', sig: 'UPPER(field)', desc: 'Convert to UPPERCASE'},
    {name: 'LOWER', sig: 'LOWER(field)', desc: 'Convert to lowercase'},
    {name: 'LEFT', sig: 'LEFT(field, n)', desc: 'First N characters'},
    {name: 'RIGHT', sig: 'RIGHT(field, n)', desc: 'Last N characters'},
    {name: 'REPLACE', sig: 'REPLACE(field,"find","new")', desc: 'Find and replace text'},
    {name: 'LEN', sig: 'LEN(field)', desc: 'Character count'},
    {name: 'CONCAT', sig: 'CONCAT(a," ",b)', desc: 'Join multiple values'},
];

export const FORMULA_TEMPLATES = {
    TRIM: {insert: 'TRIM(', cursorOffset: 5},
    UPPER: {insert: 'UPPER(', cursorOffset: 6},
    LOWER: {insert: 'LOWER(', cursorOffset: 6},
    LEFT: {insert: 'LEFT(, 10)', cursorOffset: 5},
    RIGHT: {insert: 'RIGHT(, 10)', cursorOffset: 6},
    REPLACE: {insert: 'REPLACE(, "", "")', cursorOffset: 8},
    LEN: {insert: 'LEN(', cursorOffset: 4},
    CONCAT: {insert: 'CONCAT(, " ", )', cursorOffset: 7},
};

const UNIQUE_FORMULA_SUFFIX_PATTERN = /\*+$/;

export const normalizeFormulaVariableName = (value = '') =>
    String(value).trim().replace(UNIQUE_FORMULA_SUFFIX_PATTERN, '');

const dedupeFormulaVarNames = (names = []) => Array.from(new Set(names.filter(Boolean)));

const splitVariableReference = (value = '') => {
    const normalizedValue = normalizeFormulaVariableName(value);
    const [baseName = '', ...pathSegments] = normalizedValue
        .split('.')
        .map((segment) => segment.trim())
        .filter(Boolean);

    return {
        baseName,
        pathSegments,
    };
};

const tryParseJsonContainer = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return value;
    if (typeof value !== 'string') return null;

    const trimmed = value.trim();
    if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;

    try {
        const parsed = JSON.parse(trimmed);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

const formatResolvedVariableValue = (value) => {
    if (value == null) return '';
    if (Array.isArray(value)) {
        return value.map((entry) => formatResolvedVariableValue(entry)).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

export const resolveVariableReferenceValue = (rawReference, vars = []) => {
    const {baseName, pathSegments} = splitVariableReference(rawReference);
    if (!baseName) {
        return {found: false, value: ''};
    }

    const variable = vars.find((candidate) => candidate.name === baseName);
    if (!variable) {
        return {found: false, value: ''};
    }

    if (!pathSegments.length) {
        return {
            found: true,
            value: formatResolvedVariableValue(variable.sample),
        };
    }

    let currentValue = variable.sample;

    for (const segment of pathSegments) {
        const container = tryParseJsonContainer(currentValue);
        if (!container) {
            return {found: false, value: ''};
        }

        if (Array.isArray(container)) {
            const index = Number(segment);
            if (!Number.isInteger(index) || index < 0 || index >= container.length) {
                return {found: false, value: ''};
            }
            currentValue = container[index];
            continue;
        }

        if (!Object.prototype.hasOwnProperty.call(container, segment)) {
            return {found: false, value: ''};
        }

        currentValue = container[segment];
    }

    return {
        found: true,
        value: formatResolvedVariableValue(currentValue),
    };
};

export const tokenizeFormulaText = (value = '') => {
    if (!value) return [];

    const tokens = [];
    let cursor = 0;

    while (cursor < value.length) {
        const formulaStart = value.indexOf('{{', cursor);

        if (formulaStart === -1) {
            tokens.push({
                type: 'text',
                value: value.slice(cursor),
                start: cursor,
                end: value.length,
            });
            break;
        }

        if (formulaStart > cursor) {
            tokens.push({
                type: 'text',
                value: value.slice(cursor, formulaStart),
                start: cursor,
                end: formulaStart,
            });
        }

        let index = formulaStart + 2;
        let depth = 1;
        let inString = false;
        let stringDelimiter = '';
        let escaped = false;
        let matched = false;

        while (index < value.length) {
            const char = value[index];
            const nextPair = value.slice(index, index + 2);

            if (escaped) {
                escaped = false;
                index += 1;
                continue;
            }

            if (inString) {
                if (char === '\\') {
                    escaped = true;
                } else if (char === stringDelimiter) {
                    inString = false;
                    stringDelimiter = '';
                }
                index += 1;
                continue;
            }

            if (char === '"' || char === "'") {
                inString = true;
                stringDelimiter = char;
                index += 1;
                continue;
            }

            if (nextPair === '{{') {
                depth += 1;
                index += 2;
                continue;
            }

            if (nextPair === '}}') {
                depth -= 1;
                index += 2;

                if (depth === 0) {
                    tokens.push({
                        type: 'formula',
                        value: value.slice(formulaStart, index),
                        start: formulaStart,
                        end: index,
                    });
                    cursor = index;
                    matched = true;
                    break;
                }

                continue;
            }

            index += 1;
        }

        if (!matched) {
            tokens.push({
                type: 'text',
                value: value.slice(formulaStart),
                start: formulaStart,
                end: value.length,
            });
            break;
        }
    }

    return tokens;
};

// Split a variable-reference expression on top-level pipes — the
// fallback chain separator. {{finish|custom_finish|"default"}} parses as
// three segments. Pipes inside strings or nested parens are preserved
// literally so things like {{UPPER(finish)|custom_finish}} or
// {{REPLACE(finish, "a|b", "x")|custom_finish}} still parse correctly.
const splitChainSegments = (expression) => {
    const segments = [];
    let current = '';
    let depth = 0;
    let inString = false;
    let stringDelimiter = '';

    for (const char of expression) {
        if (!inString && (char === '"' || char === "'")) {
            inString = true;
            stringDelimiter = char;
            current += char;
            continue;
        }
        if (inString && char === stringDelimiter) {
            inString = false;
            current += char;
            continue;
        }
        if (!inString && char === '(') {
            depth += 1;
            current += char;
            continue;
        }
        if (!inString && char === ')') {
            depth -= 1;
            current += char;
            continue;
        }
        if (!inString && depth === 0 && char === '|') {
            if (current.trim()) segments.push(current.trim());
            current = '';
            continue;
        }
        current += char;
    }

    if (current.trim()) segments.push(current.trim());
    return segments;
};

const parseArgs = (rawValue) => {
    const args = [];
    let current = '';
    let depth = 0;
    let inString = false;
    let stringDelimiter = '';

    for (const char of rawValue) {
        if (!inString && (char === '"' || char === "'")) {
            inString = true;
            stringDelimiter = char;
            current += char;
        } else if (inString && char === stringDelimiter) {
            inString = false;
            current += char;
        } else if (!inString && char === '(') {
            depth += 1;
            current += char;
        } else if (!inString && char === ')') {
            depth -= 1;
            current += char;
        } else if (!inString && char === ',' && depth === 0) {
            args.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    if (current.trim()) args.push(current.trim());
    return args;
};

const extractFormulaArgVarNames = (rawArg, {onlyUnique = false} = {}) => {
    const arg = rawArg.trim();
    if (!arg) return [];

    if ((arg.startsWith('"') && arg.endsWith('"')) || (arg.startsWith("'") && arg.endsWith("'"))) {
        return extractFormulaVarNames(arg.slice(1, -1), {onlyUnique});
    }

    if (/^\d+$/.test(arg)) return [];
    if (/^[A-Z_]+\(/.test(arg)) return extractFormulaExpressionVarNames(arg, {onlyUnique});

    // Pipe-fallback chain: extract names from every segment.
    if (arg.includes('|')) {
        const segments = splitChainSegments(arg);
        if (segments.length > 1) {
            return dedupeFormulaVarNames(
                segments.flatMap((segment) =>
                    extractFormulaArgVarNames(segment, {onlyUnique}),
                ),
            );
        }
    }

    const normalizedArg = normalizeFormulaVariableName(arg);
    const isUnique = normalizedArg !== arg;
    if (!normalizedArg) return [];

    return !onlyUnique || isUnique ? [normalizedArg] : [];
};

const extractFormulaExpressionVarNames = (rawExpression, {onlyUnique = false} = {}) => {
    const expression = rawExpression.trim();
    const match = expression.match(/^([A-Z_]+)\((.*)\)$/s);

    if (!match) {
        if (expression.includes('|')) {
            const segments = splitChainSegments(expression);
            if (segments.length > 1) {
                return dedupeFormulaVarNames(
                    segments.flatMap((segment) =>
                        extractFormulaArgVarNames(segment, {onlyUnique}),
                    ),
                );
            }
        }

        const normalizedExpression = normalizeFormulaVariableName(expression);
        const isUnique = normalizedExpression !== expression;
        if (!normalizedExpression) return [];

        return !onlyUnique || isUnique ? [normalizedExpression] : [];
    }

    const rawArgs = match[2];
    return dedupeFormulaVarNames(
        parseArgs(rawArgs).flatMap((arg) => extractFormulaArgVarNames(arg, {onlyUnique})),
    );
};

// Resolve a single chain segment to {found, value}. Used by both
// formula args and standalone {{...}} variable references when a pipe
// fallback chain is present. A segment is "found" only if it resolves
// to a non-empty trimmed string — empty values fall through to the
// next segment in the chain.
const resolveChainSegment = (rawSegment, vars) => {
    const segment = rawSegment.trim();
    if (!segment) return {found: false, value: ''};

    if (
        (segment.startsWith('"') && segment.endsWith('"')) ||
        (segment.startsWith("'") && segment.endsWith("'"))
    ) {
        const literal = resolveVars(segment.slice(1, -1), vars);
        return {found: literal.trim().length > 0, value: literal};
    }

    if (/^\d+$/.test(segment)) {
        return {found: true, value: segment};
    }

    if (/^[A-Z_]+\(/.test(segment)) {
        const value = resolveFormula(segment, vars);
        // resolveFormula returns the original {{...}} when nothing
        // resolved — treat that as not-found so the chain continues.
        const looksUnresolved =
            typeof value === 'string' &&
            value.startsWith('{{') &&
            value.endsWith('}}');
        return {
            found: !looksUnresolved && typeof value === 'string' && value.trim().length > 0,
            value: looksUnresolved ? '' : value,
        };
    }

    const resolved = resolveVariableReferenceValue(segment, vars);
    if (!resolved.found) return {found: false, value: ''};
    return {found: resolved.value.trim().length > 0, value: resolved.value};
};

// Resolve a pipe-fallback chain. Returns the first segment's value
// where {found: true}, or null if the expression isn't actually a chain
// (no top-level pipe).
const resolveVariableChain = (expression, vars) => {
    const segments = splitChainSegments(expression);
    if (segments.length <= 1) return null;

    for (const segment of segments) {
        const resolution = resolveChainSegment(segment, vars);
        if (resolution.found) return resolution.value;
    }
    return '';
};

const resolveArg = (rawArg, vars) => {
    const arg = rawArg.trim();

    if ((arg.startsWith('"') && arg.endsWith('"')) || (arg.startsWith("'") && arg.endsWith("'"))) {
        return resolveVars(arg.slice(1, -1), vars);
    }

    if (/^\d+$/.test(arg)) return arg;
    if (/^[A-Z_]+\(/.test(arg)) return resolveFormula(arg, vars);

    if (arg.includes('|')) {
        const chained = resolveVariableChain(arg, vars);
        if (chained !== null) return chained;
    }

    const resolvedVariable = resolveVariableReferenceValue(arg, vars);
    return resolvedVariable.found ? resolvedVariable.value : arg;
};

export const resolveFormula = (rawExpression, vars) => {
    const expression = rawExpression.trim();
    const match = expression.match(/^([A-Z_]+)\((.*)\)$/s);

    if (!match) {
        // Pipe-fallback: {{finish|custom_finish|"default"}} resolves
        // segments left-to-right; the first one that produces a
        // non-empty value wins. Falls back to the legacy single-ref
        // path when the expression doesn't contain a top-level pipe.
        if (expression.includes('|')) {
            const chained = resolveVariableChain(expression, vars);
            if (chained !== null) {
                return chained || `{{${expression}}}`;
            }
        }
        const resolvedVariable = resolveVariableReferenceValue(expression, vars);
        return resolvedVariable.found ? resolvedVariable.value : `{{${expression}}}`;
    }

    const [, fn, rawArgs] = match;
    const args = parseArgs(rawArgs);
    const value = resolveArg(args[0] || '', vars);

    switch (fn) {
        case 'TRIM':
            return value.trim();
        case 'UPPER':
            return value.toUpperCase();
        case 'LOWER':
            return value.toLowerCase();
        case 'LEFT':
            return value.slice(0, parseInt(args[1], 10) || 0);
        case 'RIGHT': {
            const count = parseInt(args[1], 10) || 0;
            return value.slice(Math.max(0, value.length - count));
        }
        case 'REPLACE':
            return value.replaceAll(resolveArg(args[1] || '', vars), resolveArg(args[2] || '', vars));
        case 'LEN':
            return String(value.length);
        case 'CONCAT':
            return args.map((arg) => resolveArg(arg, vars)).join('');
        default:
            return `{{${expression}}}`;
    }
};

export const resolveVars = (value, vars) => {
    if (!value) return value;
    return tokenizeFormulaText(value)
        .map((token) =>
            token.type === 'formula'
                ? resolveFormula(token.value.slice(2, -2).trim(), vars)
                : token.value,
        )
        .join('');
};

export const extractFormulaVarNames = (value = '', {onlyUnique = false} = {}) => {
    if (!value || !value.includes('{{')) return [];

    return dedupeFormulaVarNames(
        tokenizeFormulaText(value).flatMap((token) =>
            token.type === 'formula'
                ? extractFormulaExpressionVarNames(token.value.slice(2, -2), {onlyUnique})
                : [],
        ),
    );
};

export const extractUniqueFormulaVarNames = (value = '') => {
    return extractFormulaVarNames(value, {onlyUnique: true});
};

// Comma-delimited keyword strings (e.g. AI-suggested FAQ topic keywords)
// often list multiple {{var}} tokens that resolve to the same value for
// the current variant — e.g. `{{custom_installation_type}}` and
// `{{custom_door_installation}}` both = "Wall mount". The duplicates add
// noise without adding information. This helper splits on commas, finds
// segments that are a single {{token}}, resolves each, and drops any
// whose resolved value (case-insensitive) was already seen in the same
// string. Non-token segments and unresolved tokens are kept untouched.
export const dedupeFormulaTokensByResolvedValue = (text, vars) => {
    if (typeof text !== 'string' || !text.includes('{{')) return text;

    const seenValues = new Set();
    const segments = text
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean);

    const kept = segments.filter((segment) => {
        if (!/^\{\{[^{}]+\}\}$/.test(segment)) return true;
        const resolved = resolveVars(segment, vars);
        if (!resolved || resolved === segment) return true;
        const key = resolved.trim().toLowerCase();
        if (!key) return true;
        if (seenValues.has(key)) return false;
        seenValues.add(key);
        return true;
    });

    return kept.join(', ');
};

/**
 * Resolve all {{…}} patterns for editing, returning both the resolved text
 * and a mapping array that can later reverse the substitution.
 * Each mapping also includes position data for highlight rendering.
 */
export const resolveForEditing = (text, vars) => {
    if (!text || !text.includes('{{')) return {resolved: text, mappings: []};

    const mappings = [];
    let resolvedCursor = 0;
    const resolved = tokenizeFormulaText(text)
        .map((token) => {
            if (token.type !== 'formula') {
                resolvedCursor += token.value.length;
                return token.value;
            }

            const original = token.value;
            const value = resolveFormula(original.slice(2, -2).trim(), vars);
            mappings.push({
                resolved: value,
                original,
                start: resolvedCursor,
                length: value.length,
            });
            resolvedCursor += value.length;
            return value;
        })
        .join('');

    return {resolved, mappings};
};

/**
 * Reverse the substitution performed by resolveForEditing.
 * Replaces resolved values back with their original {{…}} patterns.
 */
export const unresolveFromEditing = (text, mappings) => {
    if (!text || !mappings?.length) return text;

    let result = text;
    // Sort longest-first to avoid partial replacements
    const sorted = [...mappings].sort((a, b) => b.resolved.length - a.resolved.length);

    for (const {resolved, original} of sorted) {
        if (resolved && result.includes(resolved)) {
            result = result.replaceAll(resolved, original);
        }
    }

    return result;
};
